import { useCallback, useEffect, useState } from 'react';
import { produce } from 'immer';
import { RootState, useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { getDiscountReasonCodes, getPlanDiscounts, updatePlanDiscounts } from '../../../logic/store/CompetitiveAllowance/CompetitiveAllowanceThunks';
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { IDiscountReasonCode } from '../../../logic/Models/Responses/DiscountReasonCodeResponse';
import { IPlanCropDiscount, IPlanHybridDiscount } from '../../../logic/Models/Responses/CompetitiveAllowanceResponse';
import { cloneDeep } from 'lodash';
import { CropKey, cropIds, cropKeys } from './CompetitiveAllowanceMain';
import { RoundPrice } from '../../../logic/Utility/CalculationUtilities';

export interface IDiscountState {
	reasonCodes?: Record<CropKey, IDiscountReasonCode[]>;
	discounts?: Record<CropKey, {
		crop: IPlanCropDiscount[];
		hybrids: IPlanHybridDiscount[];
	}>;
}

export function useDiscountEditing() 
{
	const dispatch = useAppDispatch();
	const {
		DiscountReasonCodes,
		SelectedPlan,
	} = useAppSelector((state: RootState) => ({
		DiscountReasonCodes: state.userInfo.DiscountReasonCodes,
		SelectedPlan: state.ui.SelectedPlanId ? state.grower.Growers.find(g => g.Id === state.ui.SelectedGrowerId)?.Plans.find(p => p.Id === state.ui.SelectedPlanId) : undefined,
	}));
	const GetReasonCodes = useCallback(() => dispatch(getDiscountReasonCodes()), [dispatch]);
	const GetDiscounts = useCallback(() => dispatch(getPlanDiscounts()), [dispatch]);
	const SaveDiscounts = useCallback((discounts) => dispatch(updatePlanDiscounts(discounts)), [dispatch]);

	const [discountState, setDiscountState] = useState<IDiscountState>({});

	// Load reason codes
	useEffect(() => 
	{
		if (discountState.reasonCodes === undefined) 
		{
			GetReasonCodes();
		}
	}, [discountState.reasonCodes, GetReasonCodes]);

	useEffect(() => 
	{
		if (DiscountReasonCodes && Object.keys(DiscountReasonCodes).length > 0 && !discountState.reasonCodes) 
		{
			setDiscountState(prevState => ({
				...prevState,
				reasonCodes: {
					Corn: DiscountReasonCodes[cornId].filter(code => code.IsGhxSpecific),
					Soybean: DiscountReasonCodes[soyId].filter(code => code.IsGhxSpecific),
				}
			}));
		}
	}, [DiscountReasonCodes, discountState.reasonCodes]);

	// Load discounts
	useEffect(() => 
	{
		if (discountState.discounts === undefined) 
		{
			GetDiscounts();
		}
	}, [discountState.discounts, GetDiscounts]);

	// Make a local copy for editing when loaded
	useEffect(() => 
	{
		if (SelectedPlan?.CompetitiveAllowance && !discountState.discounts) 
		{
			setDiscountState(prevState => ({
				...prevState,
				discounts: {
					Corn: {
						crop: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanCropDiscounts.filter(d => d.CropId === cornId)),
						hybrids: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanHybridDiscounts.filter(d => d.CropId === cornId)),
					},
					Soybean: {
						crop: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanCropDiscounts.filter(d => d.CropId === soyId)),
						hybrids: cloneDeep(SelectedPlan.CompetitiveAllowance.PlanHybridDiscounts.filter(d => d.CropId === soyId)),
					},
				}
			}));
		}
	}, [SelectedPlan?.CompetitiveAllowance, discountState.discounts]);

	const addCropLevelRow = useCallback((crop: CropKey) => 
	{
		setDiscountState(prevState => ({
			...prevState,
			discounts: {
				...prevState.discounts,
				[crop]: {
					...prevState.discounts[crop],
					crop: [
						...prevState.discounts[crop].crop,
						{
							Id: null,
							PlanId: SelectedPlan.Id,
							CropId: cropIds[crop],
							PerBagDiscount: 0,
							DiscountReasonCode: '',
						}
					]
				}
			}
		}));
	}, [SelectedPlan?.Id]);

	const updateCropLevelRow = useCallback((cropId: string, index: number, updatedDiscount: number, originalDiscount: number, reasonCode?: string) => 
	{
		setDiscountState(prevState => 
		{
			return produce(prevState, draft => 
			{
				draft.discounts[cropKeys[cropId]].crop[index].PerBagDiscount = RoundPrice(updatedDiscount);
				draft.discounts[cropKeys[cropId]].crop[index].DiscountReasonCode = reasonCode ? reasonCode : '';
			});
		});
	}, []);

	const deleteCropLevelRow = useCallback((cropId: string, index: number) => 
	{
		setDiscountState(prevState => 
		{
			return produce(prevState, draft => 
			{
				draft.discounts[cropKeys[cropId]].crop.splice(index, 1);
			});
		});
	}, []);

	const updateHybridLevelRow = useCallback((hybridDiscountItem: IPlanHybridDiscount) => 
	{
		setDiscountState(prevState => 
		{
			return produce(prevState, draft => 
			{
				const hybrid = draft.discounts[cropKeys[hybridDiscountItem.CropId]]
					.hybrids.find(h => h.HybridId === hybridDiscountItem.HybridId);

				if (hybrid) 
				{
					hybrid.DiscountReasonCode = hybridDiscountItem.DiscountReasonCode;
					hybrid.PerBagDiscount = RoundPrice(hybridDiscountItem.PerBagDiscount);
				}
			});
		});
	}, []);

	const save = useCallback(() => 
	{
		SaveDiscounts({
			PlanCropDiscounts: [
				...discountState.discounts?.Corn.crop || [],
				...discountState.discounts?.Soybean.crop || []
			],
			PlanHybridDiscounts: [
				...discountState.discounts?.Corn.hybrids || [],
				...discountState.discounts?.Soybean.hybrids || []
			],
		});
	}, [discountState, SaveDiscounts]);

	return {
		discountState,
		addCropLevelRow,
		updateCropLevelRow,
		deleteCropLevelRow,
		updateHybridLevelRow,
		save
	};
}
